import type { DrawnMeasure } from './drawn';
import {
  ENGRAVING,
  TUPLET_ENGRAVING,
  glyphHorizontalBounds,
  glyphVerticalBounds,
  ledgerHalfWidth,
} from './smufl';

export interface CurveObstacle {
  left: number;
  right: number;
  top: number;
  bottom: number;
  sourceId?: string;
}

/** Visible staff ink in local staff-space coordinates, for spanner clearance. */
export function curveObstacles(measures: readonly DrawnMeasure[]): CurveObstacle[] {
  const boxes: CurveObstacle[] = [];
  const glyph = (cp: number, x: number, y: number, scale: number, sourceId?: string) => {
    const h = glyphHorizontalBounds(cp),
      v = glyphVerticalBounds(cp);
    boxes.push({
      left: x + h.left * scale,
      right: x + h.right * scale,
      top: y + v.top * scale,
      bottom: y + v.bottom * scale,
      sourceId,
    });
  };
  for (const m of measures) {
    for (const n of m.notes) {
      const scale = n.grace ? ENGRAVING.graceScale : n.cue ? ENGRAVING.cueScale : 1;
      for (const head of n.heads ?? [{ ...n, dx: 0 }]) {
        glyph(n.notehead, m.x + n.x + head.dx, head.y, scale, n.sourceId);
        if (head.accidental != null)
          glyph(
            head.accidental,
            m.x + n.x + (head.accidentalDx ?? -scale),
            head.y,
            scale,
            n.sourceId,
          );
        const half = ledgerHalfWidth(n.notehead, scale);
        for (const y of head.ledger)
          boxes.push({
            left: m.x + n.x + head.dx - half,
            right: m.x + n.x + head.dx + half,
            top: y - ENGRAVING.staffLineThickness / 2,
            bottom: y + ENGRAVING.staffLineThickness / 2,
            sourceId: n.sourceId,
          });
      }
      if (n.stem) {
        const half = ENGRAVING.stemThickness / 2;
        boxes.push({
          left: m.x + n.stem.x - half,
          right: m.x + n.stem.x + half,
          top: Math.min(n.stem.y1, n.stem.y2),
          bottom: Math.max(n.stem.y1, n.stem.y2),
          sourceId: n.sourceId,
        });
        if (n.flag != null) {
          const v = glyphVerticalBounds(n.flag);
          boxes.push({
            left: m.x + n.stem.x - 0.01 * scale,
            right: m.x + n.stem.x + 1.224 * scale,
            top: n.stem.y2 + v.top * scale,
            bottom: n.stem.y2 + v.bottom * scale,
            sourceId: n.sourceId,
          });
        }
      }
      for (const mark of [...(n.artics ?? []), ...(n.ornaments ?? [])])
        glyph(mark.glyph, m.x + n.x, mark.y, scale, n.sourceId);
    }
    for (const r of m.rests) {
      const scale = r.cue ? ENGRAVING.cueScale : 1;
      glyph(r.glyph, m.x + r.x, r.y, scale, r.sourceId);
      for (const mark of r.ornaments ?? []) glyph(mark.glyph, m.x + r.x, mark.y, scale, r.sourceId);
    }
    for (const b of m.beams) {
      const half = (ENGRAVING.beamThickness * (b.grace ? ENGRAVING.graceScale : 1)) / 2;
      boxes.push({
        left: m.x + Math.min(b.x1, b.x2),
        right: m.x + Math.max(b.x1, b.x2),
        top: Math.min(b.y1, b.y2) - half,
        bottom: Math.max(b.y1, b.y2) + half,
      });
    }
    for (const t of m.tuplets) {
      const halfWidth = TUPLET_ENGRAVING.numberGap * t.label.length;
      boxes.push({
        left: m.x + t.x - halfWidth,
        right: m.x + t.x + halfWidth,
        top: t.y - TUPLET_ENGRAVING.halfHeight,
        bottom: t.y + TUPLET_ENGRAVING.halfHeight,
      });
      if (t.x1 != null && t.x2 != null) {
        const halfStroke = 0.065;
        const hook = TUPLET_ENGRAVING.hookLength;
        // The figure occupies the bracket's gap; its box above covers that
        // gap too. Hooks point towards the notes on either side of the staff.
        boxes.push({
          left: m.x + t.x1 - halfStroke,
          right: m.x + t.x2 + halfStroke,
          top: t.y - (t.below ? hook : halfStroke),
          bottom: t.y + (t.below ? halfStroke : hook),
        });
      }
    }
  }
  return boxes;
}
